<script setup lang="ts">
/**
 * @file Centered view-as preview pill, outline, and fillets at z-alert; persona menu teleports to body so it clears tldraw.
 * Store-free component shared by the board banner and the access gate.
 */
import { trackEvent } from '@@/bits/analytics'
import { __ } from '@@/bits/intl'
import { enterPreview, exitPreview, type ViewAsPreviewPersona } from '@@/bits/surface_view_as_preview'
import { getViewAsPreviewPillOutlineStyle, getViewAsPreviewPillPaintStyle } from '@@/bits/view_as_preview_pill_colors'
import OzBaseButton from '@@/library/v4/components/OzBaseButton.vue'
import OzIcon from '@@/library/v4/components/OzIcon.vue'
import { shiftUpWhenOverflow } from '@@/vuecomposables/floating'
import { offset, shift, type ComputePositionConfig } from '@floating-ui/vue'
import { defineAsyncComponent } from '@@/bits/vue'
import { computed, onMounted, ref } from 'vue'

const OzPopover = defineAsyncComponent(() => import('@@/library/v4/components/OzPopover.vue'))
const ViewAsPreviewPersonaMenu = defineAsyncComponent(() => import('@@/vuecomponents/ViewAsPreviewPersonaMenu.vue'))

const {
  testIdPrefix,
  parts,
  personas,
  previewedPersonaKey,
  selectedPersona,
  isWhiteboard,
  darkMode,
  backgroundColor,
  textColor,
  dir,
  reservedEndPx,
} = defineProps<{
  testIdPrefix: string
  parts: { prefix: string; trigger: string; suffix: string }
  personas: ViewAsPreviewPersona[]
  previewedPersonaKey: string | undefined
  selectedPersona?: ViewAsPreviewPersona
  isWhiteboard: boolean
  darkMode: boolean
  backgroundColor: string | undefined
  textColor: string | undefined
  dir?: 'ltr' | 'rtl'
  reservedEndPx?: number
}>()

const canSwitchPersona = computed((): boolean => personas.length > 1)

const xMenuOpen = ref(false)
const personaTriggerEl = ref<HTMLElement | null>(null)

const popoverConfig: ComputePositionConfig = {
  placement: 'bottom',
  strategy: 'fixed',
  // Horizontal `shift` keeps the persona menu inside the viewport when the mobile wrap
  // widens/left-shifts the trigger (~5px left clip at 390px viewports without it).
  middleware: [offset({ mainAxis: 4 }), shift({ padding: 8 }), shiftUpWhenOverflow()],
}

function toggleMenu(): void {
  xMenuOpen.value = !xMenuOpen.value
}

function onSelectPersona(persona: ViewAsPreviewPersona): void {
  xMenuOpen.value = false
  if (persona.key === previewedPersonaKey) return
  enterPreview(persona.key)
}

function onDone(): void {
  exitPreview()
}

onMounted(() => {
  if (selectedPersona == null) return
  trackEvent('Surface view as', 'Started view as preview', null, null, {
    persona: selectedPersona.key,
    role: selectedPersona.role,
  })
})

const previewPaintStyle = computed(() => getViewAsPreviewPillPaintStyle(backgroundColor, textColor))
const previewOutlineStyle = computed(() => getViewAsPreviewPillOutlineStyle(backgroundColor))

/** Reserves the given end width inline-end, so the notice centres until it would touch the
 * actions. The bar row and this overlay share `px-3`, so that width lands exactly the gap. The
 * classes below are the fallback when no width is given. */
const gridStyle = computed(() =>
  reservedEndPx != null && reservedEndPx > 0
    ? { gridTemplateColumns: `minmax(0,1fr) auto minmax(${reservedEndPx}px,1fr)` }
    : undefined,
)
</script>

<template>
  <!-- Outline, notice and fillets all take the Add-post accent colour. -->
  <div
    aria-hidden="true"
    :data-testid="`${testIdPrefix}Outline`"
    :class="['fixed inset-0 z-alert pointer-events-none', 'box-border border-[5px]']"
    :style="previewOutlineStyle"
  />

  <!-- This overlay mounts above SurfaceApp's dir wrapper, and the layout only sets `data-dir`. -->
  <div
    :dir="dir"
    :class="[
      'fixed inset-x-0 top-0 z-alert pointer-events-none',
      'grid items-start px-3',
      'grid-cols-[minmax(0,1fr)_auto_minmax(7rem,1fr)]',
      'tablet-landscape:grid-cols-[minmax(0,1fr)_auto_minmax(20rem,1fr)]',
    ]"
    :style="gridStyle"
    :data-testid="`${testIdPrefix}BannerRow`"
  >
    <div aria-hidden="true" />
    <!-- Bottom corners only, flush to the top edge: the notice hangs off the outline. -->
    <div
      role="status"
      :data-testid="`${testIdPrefix}Banner`"
      :class="[
        'relative pointer-events-auto',
        'flex items-center gap-3 min-w-0 max-w-full',
        'rounded-b-xl',
        'ps-3 pe-1 py-1',
      ]"
      :style="previewPaintStyle"
    >
      <!-- `gap-1` is the word space between prefix / trigger / suffix; string whitespace
        at a flex item's edge collapses and never reaches the screen. -->
      <div class="flex items-center gap-1 min-w-0 tablet-portrait:truncate">
        <span
          v-if="parts.prefix"
          class="text-body-small font-normal whitespace-nowrap shrink-0 hidden tablet-portrait:inline"
          :data-testid="`${testIdPrefix}BannerPrefix`"
          >{{ parts.prefix }}</span
        >
        <div ref="personaTriggerEl" class="shrink min-w-0">
          <OzBaseButton
            v-if="canSwitchPersona"
            :class="[
              // Mobile: no flex — label + chevron share one inline wrap so the icon
              // follows the last word. Flex would size the span to the longest
              // line and strand the chevron (~56px EN; worse RTL).
              // tablet-portrait+: flex + items-center restores single-line optical
              // align; inline flow keeps the horizontal attach on wrap.
              'max-w-full text-start tablet-portrait:flex tablet-portrait:items-center',
              'text-body-small font-semibold',
              'bg-transparent p-0 border-0',
              'hover-hover:hover:underline',
              'focus-visible:underline',
            ]"
            :style="{ color: textColor }"
            aria-haspopup="menu"
            :aria-expanded="xMenuOpen"
            :data-testid="`${testIdPrefix}BannerRoleTrigger`"
            @click="toggleMenu"
          >
            <!-- Pre-escaped by the adapter (incl. <bdi> around user names); `v-html` keeps
              a persona name literal without double-escaping. `whitespace-break-spaces` lets a
              long name wrap below tablet-portrait where truncate does not apply. -->
            <span
              class="whitespace-break-spaces tablet-portrait:truncate tablet-portrait:whitespace-nowrap"
              v-html="parts.trigger"
            />
            <!-- Wrapper is `inline-flex` so the chevron stays in the mobile inline wrap
              without `!important`-overriding OzIcon's root `flex`. Still a sibling of
              the truncating span (not inside it), so desktop ellipsis cannot eat it. -->
            <span class="ms-0.5 inline-flex shrink-0" :data-testid="`${testIdPrefix}BannerRoleChevron`">
              <OzIcon name="chevron_down" :size="12" />
            </span>
          </OzBaseButton>
          <!-- One audience: the phrase keeps the sentence's bold emphasis but is not a control.
            `tablet-portrait:block` is required — `truncate` sets `overflow: hidden`, which an
            inline span ignores; the button branch gets that for free from `tablet-portrait:flex`. -->
          <span
            v-else
            :class="[
              'max-w-full text-body-small font-semibold',
              'whitespace-break-spaces tablet-portrait:block tablet-portrait:truncate tablet-portrait:whitespace-nowrap',
            ]"
            :style="{ color: textColor }"
            :data-testid="`${testIdPrefix}BannerRoleTrigger`"
            v-html="parts.trigger"
          />
        </div>
        <span
          v-if="parts.suffix"
          class="text-body-small font-normal whitespace-nowrap shrink-0 hidden tablet-portrait:inline"
          :data-testid="`${testIdPrefix}BannerSuffix`"
          >{{ parts.suffix }}</span
        >
      </div>
      <OzBaseButton
        :class="[
          'shrink-0 flex items-center justify-center',
          'h-6 px-3 rounded-lg',
          'bg-transparent',
          'text-body-extra-small font-semibold',
          'hover-hover:hover:underline',
          'focus-visible:underline',
        ]"
        :style="{ color: textColor }"
        :aria-label="__('Done')"
        :data-testid="`${testIdPrefix}BannerDoneButton`"
        @click="onDone"
      >
        {{ __('Done') }}
      </OzBaseButton>

      <Teleport to="body">
        <!-- Teleport leaves the banner `:dir` wrapper; set dir here so RTL padding/radios match. -->
        <OzPopover
          v-if="canSwitchPersona && personaTriggerEl && xMenuOpen"
          :anchor="personaTriggerEl"
          :config="popoverConfig"
          :dir="dir"
          z-index-class="z-alert"
          @scrim-esc="xMenuOpen = false"
          @scrim-click="xMenuOpen = false"
        >
          <ViewAsPreviewPersonaMenu
            :personas="personas"
            :selected-key="previewedPersonaKey"
            :is-whiteboard="isWhiteboard"
            :dark-mode="darkMode"
            :test-id="`${testIdPrefix}BannerRoleMenu`"
            class="shadow-elevation-2 z-alert w-64"
            @exit="xMenuOpen = false"
            @select="onSelectPersona"
          />
        </OzPopover>
      </Teleport>

      <!-- `top-[5px]` is the outline width; the mirror is physical, so each carries its `rtl:`. -->
      <div
        aria-hidden="true"
        class="absolute -start-1.5 top-[5px] size-1.5 rtl:-scale-x-100"
        :style="backgroundColor == null ? undefined : { color: backgroundColor }"
      >
        <svg viewBox="0 0 6 6" fill="none" class="block size-full">
          <path d="M6 6V0H0C3.31371 0 6 2.68629 6 6Z" fill="currentColor" />
        </svg>
      </div>
      <div
        aria-hidden="true"
        class="absolute -end-1.5 top-[5px] size-1.5 -scale-x-100 rtl:scale-x-100"
        :style="backgroundColor == null ? undefined : { color: backgroundColor }"
      >
        <svg viewBox="0 0 6 6" fill="none" class="block size-full">
          <path d="M6 6V0H0C3.31371 0 6 2.68629 6 6Z" fill="currentColor" />
        </svg>
      </div>
    </div>
    <div aria-hidden="true" />
  </div>
</template>
